import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Info, Pencil, Printer, Trash2 } from 'lucide-react';
import {
  BILL_COLUMNS,
  formatIqd,
  fromMillidinars,
  type BillKind,
  type BillRowDto,
} from '@gc/shared';

import { PoweredOffNote } from '@/components/PoweredOffNote';
import { serviceChargeSign } from '../tabs/checkoutPreview';

export type BillAction = 'details' | 'print' | 'edit' | 'delete';

interface BillsTableProps {
  kind: BillKind;
  rows: BillRowDto[];
  currencySymbol: string;
  canEdit: boolean;
  canDelete: boolean;
  onAction: (bill: BillRowDto, action: BillAction) => void;
}

/** Which columns are action buttons rather than data. */
const ACTION_COLUMNS = new Set<string>(['details', 'print', 'edit', 'delete']);

/**
 * The Bills grid (SPEC §5.6).
 *
 * Column ORDER and MEMBERSHIP come from `BILL_COLUMNS` in `@gc/shared`, not
 * from this file. The spec is explicit that every listed Reservations column is
 * a requirement, so the list lives next to the contract the server fulfils and
 * a column cannot be quietly dropped while tidying up the UI.
 */
export function BillsTable({
  kind,
  rows,
  currencySymbol,
  canEdit,
  canDelete,
  onAction,
}: BillsTableProps) {
  const { t, i18n } = useTranslation();
  const columns = BILL_COLUMNS[kind];

  const money = (millidinars: string) =>
    formatIqd(fromMillidinars(millidinars), { currencySymbol, locale: i18n.language });

  const dateTime = (iso: string | null) => {
    if (!iso) return '—';
    const parsed = new Date(iso);
    if (Number.isNaN(parsed.getTime())) return '—';
    return new Intl.DateTimeFormat(i18n.language, {
      dateStyle: 'short',
      timeStyle: 'short',
      numberingSystem: 'latn',
    }).format(parsed);
  };

  /*
   * What a phone card shows below its invoice/total header line. A curated
   * summary, not the whole 18-column row: the card answers "whose bill, for
   * what, when, how much", and everything else is one tap away in Details.
   * Keys are BILL_COLUMNS members so the labels and cells stay the table's.
   */
  const cardFields: readonly string[] =
    (
      {
        RESERVATION: [
          'customer',
          'visitor',
          'deviceNumber',
          'startDate',
          'reservationDuration',
          'price',
          'discount',
        ],
        CASHIER: ['customer', 'visitor', 'destination', 'date', 'items', 'price', 'discount'],
        PORTAL_ORDER: [
          'customer',
          'visitor',
          'destination',
          'date',
          'items',
          'orderStatus',
          'price',
          'discount',
        ],
        REFILL: ['customer', 'date', 'refillType', 'refillAmount', 'rechargeBonus', 'balanceAfter'],
      } as Record<string, readonly string[]>
    )[kind] ?? columns.filter((key) => !ACTION_COLUMNS.has(key));
  const cardActions = columns.filter((key) => ACTION_COLUMNS.has(key));

  return (
    <>
      {/* Phone: a card per bill — an 18-column grid dragged sideways answers
        nothing at a glance, which is what a phone glance is for. */}
      <ul className="space-y-2 md:hidden">
        {rows.map((bill) => (
          <li key={bill.id} className="rounded-card border border-subtle bg-raised p-3">
            <div className="flex items-baseline gap-2">
              <span className="text-sm">{renderCell('invoiceNumber', bill)}</span>
              <span className="ms-auto text-base">{renderCell('total', bill)}</span>
            </div>
            <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
              {cardFields.map((key) => (
                <Fragment key={key}>
                  <dt className="text-xs leading-6 text-content-muted">
                    {t(`bills.columns.${key}`, key)}
                  </dt>
                  <dd className="min-w-0 truncate text-end leading-6 text-content">
                    {renderCell(key, bill)}
                  </dd>
                </Fragment>
              ))}
            </dl>
            {cardActions.length > 0 ? (
              <div className="mt-2 flex justify-end gap-1 border-t border-subtle/60 pt-2">
                {cardActions.map((key) => (
                  <Fragment key={key}>{renderCell(key, bill)}</Fragment>
                ))}
              </div>
            ) : null}
          </li>
        ))}
      </ul>

      {/* Desktop: the incumbent's full grid, exactly as it was. */}
      <div className="overflow-x-auto max-md:hidden">
        <table className="w-full min-w-max border-collapse text-sm">
          <thead>
            <tr className="border-b border-strong text-start">
              {columns.map((key) => (
                <th
                  key={key}
                  scope="col"
                  // The card's table voice: 10px, tracked caps, on the raised ink.
                  className="whitespace-nowrap bg-overlay px-3 py-2 text-start text-[10px] font-semibold uppercase tracking-[0.14em] text-content-muted"
                >
                  {/* Action columns are icons; a visible header would be noise,
                    but screen readers still need the name. */}
                  {ACTION_COLUMNS.has(key) ? (
                    <span className="sr-only">{t(`bills.columns.${key}`, key)}</span>
                  ) : (
                    t(`bills.columns.${key}`, key)
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((bill, index) => (
              <tr
                key={bill.id}
                className={`border-b border-subtle/50 ${index % 2 === 1 ? 'bg-row-odd' : 'bg-row-even'}`}
              >
                {columns.map((key) => (
                  <td key={key} className="whitespace-nowrap px-3 py-2 text-content">
                    {renderCell(key, bill)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );

  function renderCell(key: string, bill: BillRowDto) {
    switch (key) {
      case 'invoiceNumber':
        return (
          <span className="tabular font-medium">
            {bill.invoiceNumber}
            {/* The receipt it was printed on (owner, 2026-09-23) — what the
                customer quotes; the box above the grid looks it up. */}
            {bill.receiptNumber ? (
              <span
                className="block text-[10px] font-normal text-content-muted"
                data-receipt-number
              >
                {t('serviceCharge.receiptNumber', { number: bill.receiptNumber })}
              </span>
            ) : null}
          </span>
        );

      // SPEC §5.6: Customer is a registered account, Visitor is a walk-in, and
      // a row shows one or the other. Both columns always exist; the one that
      // does not apply is visibly empty rather than repeating the other name.
      case 'customer':
        return bill.customerName ?? <span className="text-content-muted">—</span>;
      case 'visitor':
        return bill.visitorName ?? <span className="text-content-muted">—</span>;

      case 'deviceNumber':
        return bill.deviceName ?? <span className="text-content-muted">—</span>;
      case 'startDate':
        return <span className="tabular">{dateTime(bill.reservation?.startedAt ?? null)}</span>;
      case 'endDate':
        // "PC turned off" under the time (owner, 2026-10-14): the sitting was
        // stopped because its PC went away, and billed up to that moment.
        return (
          <>
            <span className="tabular">{dateTime(bill.reservation?.endedAt ?? null)}</span>
            <PoweredOffNote endedReason={bill.reservation?.endedReason} />
          </>
        );
      case 'reservationDuration':
        return <span className="tabular">{bill.reservation?.duration ?? '—'}</span>;
      case 'hourlyRate':
        return (
          <span className="tabular">
            {bill.reservation ? money(bill.reservation.hourlyRateMd) : '—'}
          </span>
        );
      case 'numberOfRounds':
        return <span className="tabular">{bill.reservation?.numberOfRounds ?? 0}</span>;
      case 'roundPrice':
        return (
          <span className="tabular">
            {bill.reservation ? money(bill.reservation.roundPriceMd) : '—'}
          </span>
        );

      case 'destination':
        return bill.order?.destinationName ?? bill.deviceName ?? t('bills.destinationTab');
      case 'date':
        return <span className="tabular">{dateTime(bill.order?.placedAt ?? bill.createdAt)}</span>;
      case 'itemCount':
        return <span className="tabular">{bill.order?.itemCount ?? 0}</span>;
      case 'items':
        return (
          <span className="block max-w-64 truncate" title={bill.order?.itemSummary ?? ''}>
            {bill.order?.itemSummary ?? '—'}
          </span>
        );
      case 'orderStatus':
        return bill.order ? t(`bills.orderStatus.${bill.order.status}`, bill.order.status) : '—';

      case 'refillAmount':
        return <span className="tabular">{bill.refill ? money(bill.refill.amountMd) : '—'}</span>;
      case 'rechargeBonus':
        return <span className="tabular">{bill.refill ? money(bill.refill.bonusMd) : '—'}</span>;
      case 'totalCredited':
        return (
          <span className="tabular">{bill.refill ? money(bill.refill.totalCreditedMd) : '—'}</span>
        );
      case 'balanceAfter':
        return (
          <span className="tabular">{bill.refill ? money(bill.refill.balanceAfterMd) : '—'}</span>
        );
      case 'refillType':
        return bill.refill?.isRefund ? t('bills.refillReturn') : t('bills.refillDeposit');

      case 'price':
        return <span className="tabular">{money(bill.subtotalMd)}</span>;
      case 'discount':
        return (
          <span className={`tabular ${bill.discountMd !== '0' ? 'text-ending' : ''}`}>
            {money(bill.discountMd)}
          </span>
        );
      case 'total':
        return (
          <span className="tabular font-medium">
            {money(bill.totalMd)}
            {/* PRICE − DISCOUNT no longer reaches TOTAL on its own when a
                service charge rode the bill, so the row says what closes it. */}
            {bill.serviceChargeMd && bill.serviceChargeMd !== '0' ? (
              <span
                className="block text-[10px] font-normal text-content-muted"
                data-service-charge
              >
                {t('serviceCharge.line', { percent: bill.serviceChargePercent })}{' '}
                {serviceChargeSign(bill.serviceChargeMd)}
                {money(bill.serviceChargeMd)}
              </span>
            ) : null}
          </span>
        );

      case 'receiptEditor':
        return bill.receiptEditorName ? (
          <span title={dateTime(bill.receiptEditedAt)}>{bill.receiptEditorName}</span>
        ) : (
          <span className="text-content-muted">—</span>
        );

      case 'details':
        return (
          <IconButton label={t('common.details')} onClick={() => onAction(bill, 'details')}>
            <Info className="h-4 w-4" aria-hidden />
          </IconButton>
        );
      case 'print':
        return (
          <IconButton label={t('common.print')} onClick={() => onAction(bill, 'print')}>
            <Printer className="h-4 w-4" aria-hidden />
          </IconButton>
        );
      case 'edit':
        return (
          <IconButton
            label={t('common.edit')}
            // Disabled with a reason beats a button that silently 403s.
            disabled={!canEdit}
            disabledReason={t('bills.noEditPermission')}
            onClick={() => onAction(bill, 'edit')}
          >
            <Pencil className="h-4 w-4" aria-hidden />
          </IconButton>
        );
      case 'delete':
        return (
          <IconButton
            label={t('common.delete')}
            disabled={!canDelete}
            disabledReason={t('bills.noDeletePermission')}
            danger
            onClick={() => onAction(bill, 'delete')}
          >
            <Trash2 className="h-4 w-4" aria-hidden />
          </IconButton>
        );

      default:
        return null;
    }
  }
}

function IconButton({
  children,
  label,
  onClick,
  disabled = false,
  disabledReason,
  danger = false,
}: {
  children: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  disabledReason?: string;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={disabled ? disabledReason : label}
      aria-label={label}
      className={`grid h-7 w-7 place-items-center rounded-control transition-colors max-md:h-10 max-md:w-10 ${
        disabled
          ? 'cursor-not-allowed text-content-muted opacity-40'
          : danger
            ? 'text-danger hover:bg-hover'
            : 'text-content-secondary hover:bg-hover hover:text-content'
      }`}
    >
      {children}
    </button>
  );
}
